/**
 * Centralized Telegram Bot Configuration
 * All Telegram-related URLs and settings should use this configuration
 */

const BOT_USERNAME = 'Supaba_bot';

export const TELEGRAM_CONFIG = {
  // Bot credentials and basic info
  BOT_USERNAME,
  BOT_NAME: 'Supabank',
  
  // Core URLs
  get BOT_URL() {
    return `https://t.me/${BOT_USERNAME}`;
  },
  
  get MINI_APP_URL() {
    return `${this.BOT_URL}?startapp`;
  },
  
  // Deep link generators
  generateDeepLink: (startParam?: string) => {
    const baseUrl = `https://t.me/${BOT_USERNAME}?startapp`;
    return startParam ? `${baseUrl}=${encodeURIComponent(startParam)}` : baseUrl;
  },
  
  generateDirectMessageLink: (text?: string) => {
    const baseUrl = `https://t.me/${BOT_USERNAME}`;
    return text ? `${baseUrl}?text=${encodeURIComponent(text)}` : baseUrl;
  },
  
  // For Bridge customer auto-linking
  generateBridgeCustomerLink: (bridgeCustomerId: string) => {
    return `https://t.me/${BOT_USERNAME}?startapp=${encodeURIComponent(bridgeCustomerId)}`;
  }
};

/**
 * QR Code generation for Telegram links
 * Uses qr-code-styling for Apple-quality output (CANON 5.4c)
 */
export const generateTelegramQRCode = async (startParam?: string): Promise<string> => {
  try {
    const QRCodeStyling = (await import('qr-code-styling')).default;
    const telegramLink = TELEGRAM_CONFIG.generateDeepLink(startParam);

    console.log('[TelegramConfig] Generating QR code for:', telegramLink);

    // Get theme colors from CSS variables
    const getThemeColor = (variable: string, fallback: string): string => {
      if (typeof window === 'undefined') return fallback;
      const root = getComputedStyle(document.documentElement);
      const hsl = root.getPropertyValue(variable).trim();
      if (!hsl) return fallback;

      // Convert HSL to hex
      const [h, s, l] = hsl.split(' ').map((v, i) =>
        i === 0 ? parseFloat(v) : parseFloat(v.replace('%', ''))
      );
      const sNorm = s / 100;
      const lNorm = l / 100;
      const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
      const x = c * (1 - Math.abs((h / 60) % 2 - 1));
      const m = lNorm - c / 2;
      let r = 0, g = 0, b = 0;
      if (h >= 0 && h < 60) { r = c; g = x; }
      else if (h >= 60 && h < 120) { r = x; g = c; }
      else if (h >= 120 && h < 180) { g = c; b = x; }
      else if (h >= 180 && h < 240) { g = x; b = c; }
      else if (h >= 240 && h < 300) { r = x; b = c; }
      else { r = c; b = x; }
      const toHex = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    const foreground = getThemeColor('--foreground', '#1a1a1a');
    const background = getThemeColor('--background', '#ffffff');

    const qrCode = new QRCodeStyling({
      width: 256,
      height: 256,
      type: 'svg',
      data: telegramLink,
      margin: 2,
      qrOptions: {
        errorCorrectionLevel: 'M',
      },
      dotsOptions: {
        color: foreground,
        type: 'rounded',
      },
      cornersSquareOptions: {
        color: foreground,
        type: 'extra-rounded',
      },
      cornersDotOptions: {
        color: foreground,
        type: 'dot',
      },
      backgroundOptions: {
        color: background,
      },
    });

    // Get raw SVG data and convert to data URL
    const svgBlob = await qrCode.getRawData('svg');
    if (!svgBlob) {
      throw new Error('Failed to generate SVG blob');
    }

    const svgText = await svgBlob.text();
    const dataUrl = `data:image/svg+xml;base64,${btoa(svgText)}`;

    console.log('[TelegramConfig] QR code generated successfully');
    return dataUrl;
  } catch (error) {
    console.error('[TelegramConfig] Failed to generate QR code:', error);
    return '';
  }
};

/**
 * Utility to check if we're running in Telegram WebApp
 */
export const isTelegramWebApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  const result = !!(window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData);
  console.log('[TelegramConfig] isTelegramWebApp check:', result);
  return result;
};